import { FUNDING } from '../../i18n/el';

function localDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function deadlineState(deadline, today = new Date()) {
    if (!deadline) return 'unknown';
    const key = String(deadline).slice(0, 10);
    const current = localDateKey(today);
    if (key < current) return 'expired';
    if (key === current) return 'today';
    return 'active';
}

function dateLabel(deadline) {
    if (!deadline) return FUNDING.noCalendarDeadline;
    return new Intl.DateTimeFormat('el-GR', {
        day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC'
    }).format(new Date(deadline));
}

function amountLabel(amount) {
    if (amount === undefined || amount === null || amount === '') return null;
    return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(amount);
}

const hasValue = (value) => value !== null && value !== undefined && value !== '';

/**
 * **Έχει ο πόρος στοιχεία χρηματοδότησης;** — μία διατύπωση, δύο καταναλωτές.
 *
 * Η σύνοψη τη χρησιμοποιεί για να αποφασίσει αν θα αποδοθεί· η κάρτα του πόρου,
 * για να αποφασίσει αν θα αποδώσει το **διαχωριστικό** της. Ήταν γραμμένη μόνο
 * εδώ, οπότε το περίβλημα αποδιδόταν πάντοτε και εμφανιζόταν **γραμμή που δεν
 * χώριζε τίποτα**.
 *
 * ⚠ **Η ανάγνωση δεν εμπιστεύεται τη μορφή:** `null` από το API (η προβολή
 * ορατότητας μεταφράζει κάθε απόν πεδίο σε ρητό `null`), `undefined` από πόρο
 * χτισμένο τοπικά, **κενό αντικείμενο** από τη φόρμα. Και οι τρεις σημαίνουν
 * «καμία χρηματοδοτική πληροφορία», και η προεπιλογή παραμέτρου πιάνει μόνο τη
 * μία από τις τρεις — ήταν ακριβώς ο τρόπος με τον οποίο η οθόνη είχε πέσει
 * κάποτε σε λευκή σελίδα.
 */
export const hasFunding = (funding) =>
    Boolean(funding) && Object.values(funding).some(hasValue);

/** Συνοπτική/πλήρης προβολή χωρίς να αποκρύπτει τις ληγμένες ευκαιρίες. */
export default function FundingSummary({ funding, compact = false }) {
    // Η ανάγνωση δεν εμπιστεύεται τη μορφή: `null` από το API, `undefined` από πόρο
    // που χτίστηκε τοπικά, κενό αντικείμενο από τη φόρμα — και οι τρεις σημαίνουν
    // «καμία χρηματοδοτική πληροφορία» και καμία δεν εμφανίζει άδεια κάρτα.
    if (!hasFunding(funding)) return null;
    const state = deadlineState(funding.deadline);

    if (compact) {
        return (
            <div className="flex flex-wrap gap-2 text-xs">
                {funding.agency && <span className="rounded-full bg-accent-soft px-2 py-1">{funding.agency}</span>}
                <span className={`rounded-full px-2 py-1 ${state === 'expired' ? 'bg-danger/10 text-danger' : 'bg-accent-soft text-accent-soft-foreground'}`}>
                    {state === 'expired' ? `${FUNDING.expired}: ` : ''}{dateLabel(funding.deadline)}
                </span>
            </div>
        );
    }

    const rows = [
        [FUNDING.agency, funding.agency],
        [FUNDING.field, funding.field],
        [FUNDING.deadline, dateLabel(funding.deadline)],
        [FUNDING.amount, amountLabel(funding.amount)],
        [FUNDING.eligibleEntities, funding.eligibleEntities]
    ].filter(([, value]) => value);

    return (
        <section className="rounded-xl border border-border p-4" aria-label={FUNDING.section}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold">{FUNDING.section}</h3>
                {state === 'expired' && (
                    <span className="rounded-full bg-danger/10 px-2 py-1 text-sm font-medium text-danger">
                        {FUNDING.expired}
                    </span>
                )}
            </div>
            <dl className="grid gap-2 sm:grid-cols-2">
                {rows.map(([label, value]) => (
                    <div key={label}>
                        <dt className="text-sm text-muted">{label}</dt>
                        <dd className="whitespace-pre-line">{value}</dd>
                    </div>
                ))}
            </dl>
            {funding.terms && (
                <div className="mt-3 border-t border-border pt-3">
                    <h4 className="text-sm font-medium">{FUNDING.terms}</h4>
                    <p className="whitespace-pre-line">{funding.terms}</p>
                </div>
            )}
        </section>
    );
}
